import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { WorkWithJenna } from "@/components/WorkWithJenna";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FOUNDER_IMG = "/founder/founder-portrait.jpeg";

const beliefs = [
  { n: "01", title: "Motherhood Is Leadership™", desc: "The skills developed through motherhood are valuable leadership capabilities." },
  { n: "02", title: "Success does not require sacrifice of self", desc: "You can build a meaningful career without losing who you are." },
  { n: "03", title: "Systems create freedom", desc: "Intentional planning and supportive structures reduce overwhelm." },
  { n: "04", title: "Community creates confidence", desc: "We grow through relationships, mentorship, and shared experience." },
];

export default function About() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your message is on its way to Jenna.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="about-page">
      {/* HERO */}
      <section className="pt-40 md:pt-52 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-8">Our Founder</p>
              <h1 className="font-serif font-light text-wine text-4xl sm:text-5xl md:text-6xl leading-[1.1] tracking-tight">
                Motherhood taught her how to lead with greater purpose.
              </h1>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="relative aspect-[3/4] overflow-hidden max-w-sm ml-auto">
                <img src={FOUNDER_IMG} alt="Jenna Martins" className="w-full h-full object-cover object-top" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="py-20 md:py-28" data-testid="founder-story">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta">Her Story</p>
            </Reveal>
          </div>
          <div className="lg:col-span-8 space-y-6 text-lg text-charcoal/80 leading-relaxed">
            <Reveal>
              <p>
                Jenna Martins spent more than two decades building a successful executive career in strategy,
                growth, portfolio leadership, and organizational execution. She learned how organizations
                succeed: clear priorities matter, strong systems create sustainability, leadership requires
                adaptability, and people perform best when supported.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p>
                After becoming a mother, she realized these same principles apply to building a meaningful life
                — not just a career. Rather than accept that career success and motherhood must compete, she
                developed a new model: <span className="text-wine font-medium">The Outcome-led Professional™</span>
                {" "}— someone who delivers exceptional value through outcomes, trust, and impact rather than by
                visibility or hours worked. This personal realization became the foundation for The Thrive
                &amp; Mother Company™.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <blockquote className="font-serif italic text-2xl md:text-3xl text-wine leading-snug border-l-2 border-terracotta pl-6 mt-10">
                “I spent my career learning how to lead organizations. Motherhood taught me how to lead with
                greater purpose. The Thrive &amp; Mother Company™ exists to help women do both.”
                <span className="block not-italic text-sm text-charcoal/70 mt-6 font-sans">
                  Jenna Martins — Founder &amp; Chief Visionary Officer
                </span>
              </blockquote>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee />

      {/* MISSION / VISION */}
      <section className="bg-charcoal text-cream py-24 md:py-32" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-16">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">Mission</p>
            <p className="font-serif font-light text-2xl md:text-3xl leading-snug text-cream">
              To empower ambitious mothers with the tools, strategies, and community needed to create
              sustainable success across career, family, and personal growth.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">Vision</p>
            <p className="font-serif font-light text-2xl md:text-3xl leading-snug text-cream">
              To redefine success for generations of women by recognizing motherhood as a powerful source of
              leadership, resilience, and purpose.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CORE BELIEFS */}
      <section className="py-24 md:py-40" data-testid="core-beliefs">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-6">What We Believe</p>
            <h2 className="font-serif font-light text-wine text-4xl md:text-5xl max-w-2xl leading-tight">
              Core beliefs that shape everything we build.
            </h2>
          </Reveal>
          <div className="mt-16 divide-y divide-gold/40 border-t border-gold/40">
            {beliefs.map((b, i) => (
              <Reveal key={b.n} delay={i * 0.06}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-10 items-baseline">
                  <span className="md:col-span-1 font-serif text-gold text-2xl">{b.n}</span>
                  <h3 className="md:col-span-4 font-serif text-2xl md:text-3xl text-wine">{b.title}</h3>
                  <p className="md:col-span-7 text-charcoal/75 leading-relaxed md:text-lg">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <WorkWithJenna />

      {/* CONTACT */}
      <section className="bg-blush/30 py-24 md:py-40" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-6">Get in Touch</p>
              <h2 className="font-serif font-light text-wine text-4xl md:text-5xl leading-tight">
                We'd love to hear from you.
              </h2>
              <p className="mt-6 text-charcoal/75 leading-relaxed">
                Questions, partnerships, or just saying hello — reach out directly.
              </p>
              <a
                href="mailto:jenna@thriveandmother.com"
                data-testid="contact-email-link"
                className="link-underline inline-block mt-6 text-terracotta font-medium"
              >
                jenna@thriveandmother.com
              </a>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-charcoal/60">Name</label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="contact-name-input"
                    className="w-full bg-transparent border-b-2 border-gold/50 py-3 mt-2 text-charcoal focus:outline-none focus:border-terracotta transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-charcoal/60">Email</label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="contact-email-input"
                    className="w-full bg-transparent border-b-2 border-gold/50 py-3 mt-2 text-charcoal focus:outline-none focus:border-terracotta transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-charcoal/60">Message</label>
                  <textarea
                    required
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="contact-message-input"
                    className="w-full bg-transparent border-b-2 border-gold/50 py-3 mt-2 text-charcoal focus:outline-none focus:border-terracotta transition-colors resize-none"
                  />
                </div>
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  disabled={sending}
                  type="submit"
                  data-testid="contact-submit-button"
                  className="rounded-full bg-terracotta text-cream px-10 py-4 text-sm font-medium tracking-wide hover:bg-wine transition-colors duration-300 disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send Message"}
                </motion.button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
